import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Gift, Lock, ArrowRight } from '@phosphor-icons/react';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const { t, language } = useLanguage();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  
  const role = searchParams.get('role') || 'consumer';

  // Redirect consumers to /activer - INVITE ONLY
  useEffect(() => {
    if (role === 'consumer') {
      // Show message and redirect
      toast.info(language === 'fr' 
        ? 'L\'inscription est sur invitation uniquement. Activez votre carte cadeau reçue.' 
        : 'Registration is by invitation only. Please activate your gift card.'
      );
      navigate('/activer');
    }
  }, [role, navigate, language]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Double-check: block consumer registration
    if (role !== 'merchant') {
      toast.error(language === 'fr' 
        ? 'L\'inscription est sur invitation uniquement.' 
        : 'Registration is by invitation only.'
      );
      navigate('/activer');
      return;
    }
    
    setLoading(true);

    const result = await register(email, password, name, role);
    
    if (result.success) {
      toast.success(language === 'fr' ? 'Compte créé avec succès !' : 'Account created successfully!');
      navigate('/merchant/onboard');
    } else {
      toast.error(result.error || (language === 'fr' ? 'Échec de l\'inscription' : 'Registration failed'));
    }
    
    setLoading(false);
  };

  // If consumer, show invite-only message (before redirect)
  if (role === 'consumer') {
    return (
      <div className="min-h-screen bg-[#0F1A14] flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md text-center">
          <div className="w-20 h-20 rounded-full bg-[#C3A365]/20 flex items-center justify-center mx-auto mb-6">
            <Lock size={40} className="text-[#C3A365]" />
          </div>
          <h2 className="text-3xl tracking-tight text-[#E8EBE9] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {language === 'fr' ? 'Sur invitation uniquement' : 'By Invitation Only'}
          </h2>
          <p className="text-[#8BA89A] mb-8">
            {language === 'fr' 
              ? 'habitué est un réseau privé. Pour devenir membre, quelqu\'un doit vous offrir une carte cadeau.' 
              : 'habitué is a private network. To become a member, someone must gift you a card.'
            }
          </p>
          <div className="space-y-4">
            <button
              onClick={() => navigate('/activer')}
              className="w-full bg-[#C3A365] text-[#0F1A14] px-8 py-4 font-semibold tracking-wide text-sm flex items-center justify-center gap-2"
            >
              <Gift size={20} />
              {language === 'fr' ? 'Activer ma carte' : 'Activate my card'}
              <ArrowRight size={18} />
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full border border-[#3E5248] text-white px-8 py-4 font-medium text-sm hover:border-[#C3A365]/50 transition-colors"
            >
              {language === 'fr' ? 'Retour à l\'accueil' : 'Back to home'}
            </button>
          </div>
          <p className="text-[#8BA89A] text-sm mt-8">
            {language === 'fr' ? 'Vous êtes commerçant ?' : 'Are you a merchant?'}{' '}
            <Link to="/register?role=merchant" className="text-[#C3A365] hover:underline">
              {language === 'fr' ? 'Inscrivez-vous ici' : 'Register here'}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // Merchant registration form
  return (
    <div className="min-h-screen bg-[#18261E] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <Link to="/" className="brand-logo text-4xl inline-block" data-testid="brand-logo">habitué</Link>
          <h2 className="text-3xl tracking-tight text-[#E8EBE9] mt-8 mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {language === 'fr' ? 'Devenir partenaire' : 'Join as Merchant'}
          </h2>
          <p className="text-[#889990]">
            {language === 'fr' ? 'Acceptez les paiements habitué' : 'Start accepting habitué payments'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
          <div>
            <label className="block text-xs tracking-[0.2em] uppercase text-[#C3A365] font-semibold mb-3">
              {language === 'fr' ? 'Nom complet' : 'Full Name'}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full bg-[#283D32] border border-[#3E5248] px-4 py-4 text-base text-[#E8EBE9] placeholder:text-[#889990] focus:border-[#145A3A] focus:ring-0 outline-none transition-all"
              placeholder={language === 'fr' ? 'Votre nom' : 'Your name'}
              data-testid="register-name-input"
            />
          </div>

          <div>
            <label className="block text-xs tracking-[0.2em] uppercase text-[#C3A365] font-semibold mb-3">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-[#283D32] border border-[#3E5248] px-4 py-4 text-base text-[#E8EBE9] placeholder:text-[#889990] focus:border-[#145A3A] focus:ring-0 outline-none transition-all"
              placeholder="vous@exemple.com"
              data-testid="register-email-input"
            />
          </div>

          <div>
            <label className="block text-xs tracking-[0.2em] uppercase text-[#C3A365] font-semibold mb-3">
              {language === 'fr' ? 'Mot de passe' : 'Password'}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full bg-[#283D32] border border-[#3E5248] px-4 py-4 text-base text-[#E8EBE9] placeholder:text-[#889990] focus:border-[#145A3A] focus:ring-0 outline-none transition-all"
              placeholder="••••••••"
              data-testid="register-password-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#C3A365] text-[#18261E] hover:shadow-[0_8px_32px_rgba(195,163,101,0.4)] px-8 py-4 font-semibold tracking-wide text-sm transition-all disabled:opacity-50"
            data-testid="register-submit-button"
          >
            {loading 
              ? (language === 'fr' ? 'Création...' : 'Creating...') 
              : (language === 'fr' ? 'Créer un compte' : 'Create Account')
            }
          </button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-[#889990]">
            {language === 'fr' ? 'Déjà un compte ?' : 'Already have an account?'}{' '}
            <Link to="/login" className="text-[#C3A365] hover:text-[#D4B876] font-medium transition-colors" data-testid="login-link">
              {language === 'fr' ? 'Connexion' : 'Sign in'}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
